import {
  decodeEnvelope,
  ENVELOPE_PREFIX,
  MAX_PAYLOAD_BYTES,
} from './envelope.js';

// Experimental phone-compatible optical framing. It borrows Manchester's
// transition-per-bit property, but deliberately makes no IEEE PHY claim: Web
// torch timing is negotiated by the browser/driver and is orders of magnitude
// slower than the C-OOK optical clocks described by IEEE contributions.
export const TORCH_OOK_PROFILE = 'LUMICHAT_TORCH_MANCHESTER_EXPERIMENTAL';
export const TORCH_OOK_VERSION = 1;

const PREAMBLE_BIT_COUNT = 32;
const PREAMBLE_BITS = new Uint8Array(PREAMBLE_BIT_COUNT).fill(1);
const SYNC_BYTES = Uint8Array.of(0xd3, 0x91, 0xc5, 0xa7);
const SYNC_BITS = bytesToBits(SYNC_BYTES);
const HEADER_BYTES = 5;
const HEADER_BITS = HEADER_BYTES * 8;
const LEAD_IN_CHIPS = 8;
const TAIL_CHIPS = 8;
const PREFIX_BITS = PREAMBLE_BIT_COUNT + SYNC_BITS.length;
const PREFIX_CHIPS = PREFIX_BITS * 2;
const MAX_ENVELOPE_BYTES = MAX_PAYLOAD_BYTES + 19;
const MIN_ENVELOPE_BYTES = 19;
export const TORCH_OOK_MAX_FRAME_CHIPS = LEAD_IN_CHIPS
  + (PREFIX_BITS + HEADER_BITS + MAX_ENVELOPE_BYTES * 8) * 2
  + TAIL_CHIPS;

const DEFAULT_DECODER = Object.freeze({
  phaseSteps: 12,
  minContrast: 0.04,
  minPairDeltaRatio: 0.12,
  minPreambleScore: 0.875,
  minSyncScore: 0.875,
  localThresholdRadius: 24,
});

/**
 * Selects a conservative chip duration from measurements, not from a nominal
 * hardware capability. Slow devices remain usable at a lower bitrate.
 */
export function chooseTorchOokTiming({
  torchP95Ms,
  receiverFps,
  frameIntervalP95Ms = null,
  torchSafetyFactor = 1.5,
  targetSamplesPerChip = 2.25,
  quantizationMs = 1,
  minimumChipMs = 4,
  maximumChipMs = Infinity,
} = {}) {
  assertPositive(torchP95Ms, 'torchP95Ms');
  assertPositive(receiverFps, 'receiverFps');
  if (frameIntervalP95Ms !== null) assertPositive(frameIntervalP95Ms, 'frameIntervalP95Ms');
  assertPositive(torchSafetyFactor, 'torchSafetyFactor');
  assertPositive(targetSamplesPerChip, 'targetSamplesPerChip');
  assertPositive(quantizationMs, 'quantizationMs');
  assertPositive(minimumChipMs, 'minimumChipMs');
  if (maximumChipMs !== Infinity) {
    assertPositive(maximumChipMs, 'maximumChipMs');
    if (maximumChipMs < minimumChipMs) throw new RangeError('maximumChipMs inférieur à minimumChipMs');
  }

  const nominalFrameIntervalMs = 1000 / receiverFps;
  const effectiveFrameIntervalMs = Math.max(
    nominalFrameIntervalMs,
    frameIntervalP95Ms || 0,
  );
  const torchBudgetMs = torchP95Ms * torchSafetyFactor;
  const cameraBudgetMs = effectiveFrameIntervalMs * targetSamplesPerChip;
  const unquantizedChipMs = Math.max(minimumChipMs, torchBudgetMs, cameraBudgetMs);
  const chipMs = Math.ceil(unquantizedChipMs / quantizationMs) * quantizationMs;
  if (chipMs > maximumChipMs) {
    const error = new RangeError(
      `cadence mesurée ${chipMs} ms/chip hors plage RX (maximum ${maximumChipMs} ms/chip)`,
    );
    error.code = 'TORCH_TIMING_OUT_OF_RANGE';
    error.details = { chipMs, maximumChipMs, torchBudgetMs, cameraBudgetMs };
    throw error;
  }
  const limitingFactor = cameraBudgetMs >= torchBudgetMs && cameraBudgetMs >= minimumChipMs
    ? 'camera'
    : torchBudgetMs >= minimumChipMs
      ? 'torch'
      : 'minimum';
  const rawBitrateBps = 1000 / (2 * chipMs);

  return Object.freeze({
    profile: TORCH_OOK_PROFILE,
    standardConformance: 'none-claimed',
    chipMs,
    rawBitrateBps,
    limitingFactor,
    torchP95Ms,
    torchSafetyFactor,
    receiverFps,
    nominalFrameIntervalMs,
    frameIntervalP95Ms,
    effectiveFrameIntervalMs,
    targetSamplesPerChip,
    maximumChipMs,
    estimatedSamplesPerChip: chipMs / effectiveFrameIntervalMs,
    torchHeadroom: chipMs / torchP95Ms,
    warning: rawBitrateBps < 2
      ? 'Cadence très lente, mais mesurée comme exploitable; confirmer sur deux appareils.'
      : null,
  });
}

/**
 * Derives a receiver buffer that can retain the largest legal envelope at the
 * slowest timing advertised by the matching transmitter.
 */
export function torchOokBufferRequirements({
  maximumChipMs,
  maximumFps,
  guardChips = 32,
  sampleHeadroom = 1.25,
} = {}) {
  assertPositive(maximumChipMs, 'maximumChipMs');
  assertPositive(maximumFps, 'maximumFps');
  if (!Number.isInteger(guardChips) || guardChips < 0) {
    throw new RangeError('guardChips doit être un entier >= 0');
  }
  if (!Number.isFinite(sampleHeadroom) || sampleHeadroom < 1) {
    throw new RangeError('sampleHeadroom doit être >= 1');
  }
  const maxBufferMs = (TORCH_OOK_MAX_FRAME_CHIPS + guardChips) * maximumChipMs;
  const maxBufferSamples = Math.ceil(maxBufferMs / 1000 * maximumFps * sampleHeadroom);
  return Object.freeze({
    maxFrameChips: TORCH_OOK_MAX_FRAME_CHIPS,
    guardChips,
    maximumChipMs,
    maximumFps,
    sampleHeadroom,
    maxBufferMs,
    maxBufferSamples,
  });
}

/**
 * Encodes an already CRC-protected LumiChat envelope into OOK chips.
 * Manchester convention used here: bit 0 -> [dark, light], bit 1 -> [light, dark].
 */
export function encodeTorchOokFrame(envelopeBytes, { chipMs } = {}) {
  assertPositive(chipMs, 'chipMs');
  if (!(envelopeBytes instanceof Uint8Array)) {
    throw new TypeError('envelopeBytes doit être un Uint8Array');
  }
  if (envelopeBytes.length < MIN_ENVELOPE_BYTES || envelopeBytes.length > MAX_ENVELOPE_BYTES) {
    throw new RangeError(`longueur enveloppe hors plage ${MIN_ENVELOPE_BYTES}..${MAX_ENVELOPE_BYTES}`);
  }

  const envelope = decodeEnvelope(bytesToEnvelopeText(envelopeBytes));
  if (!envelope.ok) throw new RangeError(`enveloppe invalide: ${envelope.code}`);

  const length = envelopeBytes.length;
  const inverseLength = (~length) & 0xffff;
  const header = Uint8Array.of(
    TORCH_OOK_VERSION,
    length >>> 8,
    length,
    inverseLength >>> 8,
    inverseLength,
  );
  const framedBits = concatUint8Arrays(
    PREAMBLE_BITS,
    SYNC_BITS,
    bytesToBits(header),
    bytesToBits(envelopeBytes),
  );
  const encoded = manchesterEncode(framedBits);
  const chips = new Uint8Array(LEAD_IN_CHIPS + encoded.length + TAIL_CHIPS);
  chips.set(encoded, LEAD_IN_CHIPS);

  const frameDurationMs = chips.length * chipMs;
  return {
    profile: TORCH_OOK_PROFILE,
    standardConformance: 'none-claimed',
    chips,
    chipMs,
    frameDurationMs,
    rawBitrateBps: 1000 / (2 * chipMs),
    usefulEnvelopeBitrateBps: (envelopeBytes.length * 8 * 1000) / frameDurationMs,
    envelopeBytes: envelopeBytes.slice(),
    envelope,
    layout: Object.freeze({
      leadInChips: LEAD_IN_CHIPS,
      preambleStartChip: LEAD_IN_CHIPS,
      syncStartChip: LEAD_IN_CHIPS + PREAMBLE_BIT_COUNT * 2,
      headerStartChip: LEAD_IN_CHIPS + PREFIX_CHIPS,
      envelopeStartChip: LEAD_IN_CHIPS + (PREFIX_BITS + HEADER_BITS) * 2,
      tailChips: TAIL_CHIPS,
    }),
  };
}

/**
 * Decodes timestamped optical samples. Each sample is
 * `{ timestampMs: monotonicMilliseconds, value: normalizedLuma }`.
 * No received frame is returned unless the existing LumiChat CRC32C envelope
 * validates successfully.
 */
export function decodeTorchOokSamples(samples, {
  chipMs,
  phaseSteps = DEFAULT_DECODER.phaseSteps,
  minContrast = DEFAULT_DECODER.minContrast,
  minPairDeltaRatio = DEFAULT_DECODER.minPairDeltaRatio,
  minPreambleScore = DEFAULT_DECODER.minPreambleScore,
  minSyncScore = DEFAULT_DECODER.minSyncScore,
  localThresholdRadius = DEFAULT_DECODER.localThresholdRadius,
} = {}) {
  assertPositive(chipMs, 'chipMs');
  if (!Number.isInteger(phaseSteps) || phaseSteps < 2 || phaseSteps > 64) {
    throw new RangeError('phaseSteps doit être un entier entre 2 et 64');
  }
  assertRatio(minPreambleScore, 'minPreambleScore');
  assertRatio(minSyncScore, 'minSyncScore');
  assertPositive(minContrast, 'minContrast');
  assertPositive(minPairDeltaRatio, 'minPairDeltaRatio');
  if (!Number.isInteger(localThresholdRadius) || localThresholdRadius < 2) {
    throw new RangeError('localThresholdRadius doit être un entier >= 2');
  }

  const normalized = normalizeSamples(samples);
  const diagnostics = {
    profile: TORCH_OOK_PROFILE,
    standardConformance: 'none-claimed',
    chipMs,
    rawBitrateBps: 1000 / (2 * chipMs),
    inputSamples: Array.isArray(samples) ? samples.length : 0,
    validSamples: normalized.samples.length,
    invalidSamples: normalized.invalidSamples,
    nonMonotonicSamples: normalized.nonMonotonicSamples,
    timestampSpanMs: normalized.spanMs,
    measuredFps: normalized.measuredFps,
    phaseSteps,
    candidatesTested: 0,
    rejections: {},
    bestCandidate: null,
  };

  if (normalized.samples.length < 3) {
    return reject('NO_SAMPLES', 'Pas assez de samples timestampés valides', diagnostics);
  }
  if (normalized.nonMonotonicSamples > 0) {
    return reject('BAD_TIMESTAMPS', 'Les timestamps doivent être strictement croissants', diagnostics);
  }
  if (normalized.spanMs < (PREFIX_CHIPS - 2) * chipMs) {
    return reject('INSUFFICIENT_DURATION', 'Capture trop courte pour préambule et synchronisation', diagnostics);
  }

  const values = normalized.samples.map((sample) => sample.value).sort((a, b) => a - b);
  const lowLevel = quantile(values, 0.1);
  const highLevel = quantile(values, 0.9);
  const contrast = highLevel - lowLevel;
  Object.assign(diagnostics, {
    levels: {
      low: lowLevel,
      high: highLevel,
      threshold: (lowLevel + highLevel) / 2,
      contrast,
    },
  });
  if (contrast < minContrast) {
    return reject('LOW_CONTRAST', `Contraste ${round(contrast)} inférieur au minimum ${minContrast}`, diagnostics);
  }

  const pairMinDelta = Math.max(minContrast * 0.25, contrast * minPairDeltaRatio);
  let bestFailure = null;
  const successes = [];

  for (let phaseIndex = 0; phaseIndex < phaseSteps; phaseIndex += 1) {
    const phaseMs = (phaseIndex * chipMs) / phaseSteps;
    const timeline = binSamples(normalized.samples, chipMs, phaseMs);
    if (timeline.levels.length < PREFIX_CHIPS) continue;
    const phaseStats = sampleBinStats(timeline.counts);

    for (let startChip = 0; startChip <= timeline.levels.length - PREFIX_CHIPS; startChip += 1) {
      const preamble = decodeManchesterAt(timeline.levels, startChip, PREAMBLE_BIT_COUNT, {
        expected: PREAMBLE_BITS,
        pairMinDelta,
        minContrast,
        localThresholdRadius,
        allowSingleChipRecovery: false,
      });
      if (preamble.score < minPreambleScore) continue;

      const syncStart = startChip + PREAMBLE_BIT_COUNT * 2;
      const sync = decodeManchesterAt(timeline.levels, syncStart, SYNC_BITS.length, {
        expected: SYNC_BITS,
        pairMinDelta,
        minContrast,
        localThresholdRadius,
        allowSingleChipRecovery: false,
      });
      diagnostics.candidatesTested += 1;
      const baseCandidate = {
        phaseIndex,
        phaseMs,
        startChip,
        preambleScore: preamble.score,
        syncScore: sync.score,
        samplesPerChip: phaseStats,
      };
      updateBestCandidate(diagnostics, baseCandidate);
      if (sync.score < minSyncScore) {
        noteRejection(diagnostics, 'NO_SYNC');
        bestFailure = preferFailure(bestFailure, failure(2, 'NO_SYNC', 'Mot de synchronisation non confirmé', baseCandidate));
        continue;
      }

      const headerStart = syncStart + SYNC_BITS.length * 2;
      const headerDecoded = decodeManchesterAt(timeline.levels, headerStart, HEADER_BITS, {
        pairMinDelta,
        minContrast,
        localThresholdRadius,
        allowSingleChipRecovery: true,
      });
      if (!headerDecoded.ok) {
        noteRejection(diagnostics, 'MANCHESTER_HEADER');
        bestFailure = preferFailure(bestFailure, failure(3, 'MANCHESTER_HEADER', 'Header Manchester incomplet ou ambigu', {
          ...baseCandidate,
          invalidPairs: headerDecoded.invalidPairs,
          recoveredPairs: headerDecoded.recoveredPairs,
        }));
        continue;
      }
      const header = bitsToBytes(headerDecoded.bits);
      if (header[0] !== TORCH_OOK_VERSION) {
        noteRejection(diagnostics, 'BAD_VERSION');
        bestFailure = preferFailure(bestFailure, failure(4, 'BAD_VERSION', `Version torch inconnue ${header[0]}`, baseCandidate));
        continue;
      }
      const length = (header[1] << 8) | header[2];
      const inverseLength = (header[3] << 8) | header[4];
      if (((~length) & 0xffff) !== inverseLength) {
        noteRejection(diagnostics, 'BAD_LENGTH_COMPLEMENT');
        bestFailure = preferFailure(bestFailure, failure(5, 'BAD_LENGTH_COMPLEMENT', 'Longueur et complément incohérents', baseCandidate));
        continue;
      }
      if (length < MIN_ENVELOPE_BYTES || length > MAX_ENVELOPE_BYTES) {
        noteRejection(diagnostics, 'BAD_LENGTH');
        bestFailure = preferFailure(bestFailure, failure(6, 'BAD_LENGTH', `Longueur enveloppe hors plage: ${length}`, baseCandidate));
        continue;
      }

      const envelopeStart = headerStart + HEADER_BITS * 2;
      const envelopeBits = length * 8;
      if (envelopeStart + envelopeBits * 2 > timeline.levels.length) {
        noteRejection(diagnostics, 'TRUNCATED');
        bestFailure = preferFailure(bestFailure, failure(7, 'TRUNCATED', 'Fin de capture avant la fin de l’enveloppe', baseCandidate));
        continue;
      }
      const bodyDecoded = decodeManchesterAt(timeline.levels, envelopeStart, envelopeBits, {
        pairMinDelta,
        minContrast,
        localThresholdRadius,
        allowSingleChipRecovery: true,
      });
      if (!bodyDecoded.ok) {
        noteRejection(diagnostics, 'MANCHESTER_BODY');
        bestFailure = preferFailure(bestFailure, failure(8, 'MANCHESTER_BODY', 'Payload Manchester incomplet ou ambigu', {
          ...baseCandidate,
          invalidPairs: bodyDecoded.invalidPairs,
          recoveredPairs: bodyDecoded.recoveredPairs,
        }));
        continue;
      }

      const envelopeBytes = bitsToBytes(bodyDecoded.bits);
      const envelope = decodeEnvelope(bytesToEnvelopeText(envelopeBytes));
      if (!envelope.ok) {
        noteRejection(diagnostics, envelope.code);
        bestFailure = preferFailure(bestFailure, failure(9, envelope.code, envelope.reason, {
          ...baseCandidate,
          recoveredPairs: headerDecoded.recoveredPairs + bodyDecoded.recoveredPairs,
        }));
        continue;
      }
      successes.push({
        envelope,
        envelopeBytes,
        diagnostics: {
          ...baseCandidate,
          threshold: diagnostics.levels.threshold,
          contrast,
          pairMinDelta,
          invalidPairs: 0,
          recoveredPairs: headerDecoded.recoveredPairs + bodyDecoded.recoveredPairs,
          decodedBytes: length,
        },
      });
    }
  }

  if (!successes.length) {
    if (bestFailure) {
      diagnostics.bestCandidate = bestFailure.details;
      return reject(bestFailure.code, bestFailure.reason, diagnostics);
    }
    return reject('NO_PREAMBLE', 'Aucun préambule Manchester exploitable', diagnostics);
  }

  successes.sort((left, right) => candidateQuality(right.diagnostics) - candidateQuality(left.diagnostics));
  const selected = successes[0];
  return {
    ok: true,
    code: 'OK',
    envelope: selected.envelope,
    envelopeBytes: selected.envelopeBytes,
    diagnostics: {
      ...diagnostics,
      ...selected.diagnostics,
      validCandidates: successes.length,
    },
  };
}

/**
 * Cheap timing probe used before a full frame decode. It inspects only the
 * Manchester preamble and sync word; callers MUST still require a successful
 * decodeTorchOokSamples() result, including the envelope CRC32C.
 */
export function probeTorchOokPreamble(samples, {
  chipMs,
  phaseSteps = 6,
  minContrast = DEFAULT_DECODER.minContrast,
  minPairDeltaRatio = DEFAULT_DECODER.minPairDeltaRatio,
} = {}) {
  assertPositive(chipMs, 'chipMs');
  if (!Number.isInteger(phaseSteps) || phaseSteps < 2 || phaseSteps > 24) {
    throw new RangeError('phaseSteps doit être un entier entre 2 et 24');
  }
  const normalized = normalizeSamples(samples);
  const base = {
    ok: false,
    chipMs,
    sampleCount: normalized.samples.length,
    phaseSteps,
    preambleScore: 0,
    syncScore: 0,
    combinedScore: 0,
    phaseMs: null,
    startChip: null,
  };
  if (normalized.samples.length < 3 || normalized.nonMonotonicSamples > 0
    || normalized.spanMs < (PREFIX_CHIPS - 2) * chipMs) return base;

  const values = normalized.samples.map((sample) => sample.value).sort((a, b) => a - b);
  const low = quantile(values, 0.1);
  const high = quantile(values, 0.9);
  const contrast = high - low;
  if (contrast < minContrast) return { ...base, contrast };
  const pairMinDelta = Math.max(minContrast * 0.25, contrast * minPairDeltaRatio);
  let best = { ...base, contrast, pairMinDelta };

  for (let phaseIndex = 0; phaseIndex < phaseSteps; phaseIndex += 1) {
    const phaseMs = (phaseIndex * chipMs) / phaseSteps;
    const timeline = binSamples(normalized.samples, chipMs, phaseMs);
    for (let startChip = 0; startChip <= timeline.levels.length - PREFIX_CHIPS; startChip += 1) {
      const preamble = decodeManchesterAt(timeline.levels, startChip, PREAMBLE_BIT_COUNT, {
        expected: PREAMBLE_BITS,
        pairMinDelta,
        minContrast,
        localThresholdRadius: DEFAULT_DECODER.localThresholdRadius,
        allowSingleChipRecovery: false,
      });
      // Random data averages 0.5. This loose gate saves sync work while still
      // retaining candidates whose coarse chip duration is not yet exact.
      if (preamble.score < 0.65) continue;
      const sync = decodeManchesterAt(
        timeline.levels,
        startChip + PREAMBLE_BIT_COUNT * 2,
        SYNC_BITS.length,
        {
          expected: SYNC_BITS,
          pairMinDelta,
          minContrast,
          localThresholdRadius: DEFAULT_DECODER.localThresholdRadius,
          allowSingleChipRecovery: false,
        },
      );
      const combinedScore = preamble.score * 0.45 + sync.score * 0.55;
      if (combinedScore > best.combinedScore) {
        best = {
          ...base,
          ok: preamble.score >= DEFAULT_DECODER.minPreambleScore
            && sync.score >= DEFAULT_DECODER.minSyncScore,
          contrast,
          pairMinDelta,
          preambleScore: preamble.score,
          syncScore: sync.score,
          combinedScore,
          phaseIndex,
          phaseMs,
          startChip,
        };
      }
    }
  }
  return best;
}

function normalizeSamples(input) {
  const source = Array.isArray(input) ? input : [];
  const samples = [];
  let invalidSamples = 0;
  let nonMonotonicSamples = 0;
  let previous = -Infinity;
  for (const sample of source) {
    const timestampMs = Number(sample?.timestampMs);
    const value = Number(sample?.value);
    if (!Number.isFinite(timestampMs) || !Number.isFinite(value)) {
      invalidSamples += 1;
      continue;
    }
    if (timestampMs <= previous) nonMonotonicSamples += 1;
    previous = timestampMs;
    samples.push({ timestampMs, value });
  }
  const spanMs = samples.length >= 2 ? samples.at(-1).timestampMs - samples[0].timestampMs : 0;
  return {
    samples,
    invalidSamples,
    nonMonotonicSamples,
    spanMs,
    measuredFps: spanMs > 0 ? ((samples.length - 1) * 1000) / spanMs : null,
  };
}

function binSamples(samples, chipMs, phaseMs) {
  const originMs = samples[0].timestampMs - phaseMs;
  const lastIndex = Math.floor((samples.at(-1).timestampMs - originMs) / chipMs);
  const buckets = Array.from({ length: lastIndex + 1 }, () => []);
  for (const sample of samples) {
    const index = Math.floor((sample.timestampMs - originMs) / chipMs);
    if (index >= 0 && index < buckets.length) buckets[index].push(sample.value);
  }
  return {
    originMs,
    levels: buckets.map((bucket) => bucket.length ? median(bucket) : null),
    counts: buckets.map((bucket) => bucket.length),
  };
}

function decodeManchesterAt(levels, startChip, bitCount, options) {
  const bits = new Uint8Array(bitCount);
  let matches = 0;
  let invalidPairs = 0;
  let recoveredPairs = 0;
  for (let bitIndex = 0; bitIndex < bitCount; bitIndex += 1) {
    const chipIndex = startChip + bitIndex * 2;
    const decoded = decodeManchesterPair(levels, chipIndex, options);
    if (decoded.bit === null) {
      invalidPairs += 1;
      continue;
    }
    bits[bitIndex] = decoded.bit;
    if (decoded.recovered) recoveredPairs += 1;
    if (!options.expected || decoded.bit === options.expected[bitIndex]) matches += 1;
  }
  return {
    ok: invalidPairs === 0,
    bits,
    invalidPairs,
    recoveredPairs,
    score: options.expected ? matches / bitCount : (bitCount - invalidPairs) / bitCount,
  };
}

function decodeManchesterPair(levels, chipIndex, options) {
  const first = levels[chipIndex];
  const second = levels[chipIndex + 1];
  if (first !== null && second !== null && Math.abs(first - second) >= options.pairMinDelta) {
    return { bit: first > second ? 1 : 0, recovered: false };
  }
  if (!options.allowSingleChipRecovery) return { bit: null, recovered: false };

  const local = localLevels(levels, chipIndex, options.localThresholdRadius);
  if (!local || local.contrast < options.minContrast) return { bit: null, recovered: false };
  const margin = local.contrast * 0.12;
  if (first !== null && second !== null) {
    if (first >= local.threshold + margin && second <= local.threshold - margin) return { bit: 1, recovered: true };
    if (first <= local.threshold - margin && second >= local.threshold + margin) return { bit: 0, recovered: true };
    return { bit: null, recovered: false };
  }
  if (first !== null) {
    if (first >= local.threshold + margin) return { bit: 1, recovered: true };
    if (first <= local.threshold - margin) return { bit: 0, recovered: true };
  }
  if (second !== null) {
    if (second >= local.threshold + margin) return { bit: 0, recovered: true };
    if (second <= local.threshold - margin) return { bit: 1, recovered: true };
  }
  return { bit: null, recovered: false };
}

function localLevels(levels, center, radius) {
  const values = [];
  for (let index = Math.max(0, center - radius); index <= Math.min(levels.length - 1, center + radius); index += 1) {
    if (levels[index] !== null) values.push(levels[index]);
  }
  if (values.length < 4) return null;
  values.sort((a, b) => a - b);
  const low = quantile(values, 0.2);
  const high = quantile(values, 0.8);
  return { low, high, threshold: (low + high) / 2, contrast: high - low };
}

function sampleBinStats(counts) {
  const sorted = counts.filter((count) => count > 0).sort((a, b) => a - b);
  return {
    populated: sorted.length,
    empty: counts.length - sorted.length,
    p10: quantile(sorted, 0.1),
    median: quantile(sorted, 0.5),
    p90: quantile(sorted, 0.9),
  };
}

function manchesterEncode(bits) {
  const chips = new Uint8Array(bits.length * 2);
  for (let index = 0; index < bits.length; index += 1) {
    const bit = bits[index] ? 1 : 0;
    chips[index * 2] = bit;
    chips[index * 2 + 1] = bit ^ 1;
  }
  return chips;
}

function bytesToBits(bytes) {
  const bits = new Uint8Array(bytes.length * 8);
  for (let index = 0; index < bytes.length; index += 1) {
    for (let bit = 0; bit < 8; bit += 1) bits[index * 8 + bit] = (bytes[index] >>> (7 - bit)) & 1;
  }
  return bits;
}

function bitsToBytes(bits) {
  if (bits.length % 8 !== 0) throw new RangeError('nombre de bits non multiple de 8');
  const bytes = new Uint8Array(bits.length / 8);
  for (let index = 0; index < bits.length; index += 1) {
    bytes[Math.floor(index / 8)] |= bits[index] << (7 - (index % 8));
  }
  return bytes;
}

function bytesToEnvelopeText(bytes) {
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return `${ENVELOPE_PREFIX}${btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '')}`;
}

function concatUint8Arrays(...arrays) {
  const output = new Uint8Array(arrays.reduce((sum, array) => sum + array.length, 0));
  let offset = 0;
  for (const array of arrays) {
    output.set(array, offset);
    offset += array.length;
  }
  return output;
}

function normalizeFailureDetails(details) {
  return details ? { ...details } : null;
}

function failure(stage, code, reason, details) {
  return { stage, code, reason, details: normalizeFailureDetails(details) };
}

function preferFailure(current, candidate) {
  if (!current || candidate.stage > current.stage) return candidate;
  if (candidate.stage < current.stage) return current;
  return candidateQuality(candidate.details) > candidateQuality(current.details) ? candidate : current;
}

function candidateQuality(candidate = {}) {
  return (candidate.preambleScore || 0) * 2
    + (candidate.syncScore || 0) * 3
    - (candidate.recoveredPairs || 0) * 0.001
    - (candidate.invalidPairs || 0) * 0.01;
}

function updateBestCandidate(diagnostics, candidate) {
  if (!diagnostics.bestCandidate || candidateQuality(candidate) > candidateQuality(diagnostics.bestCandidate)) {
    diagnostics.bestCandidate = { ...candidate };
  }
}

function noteRejection(diagnostics, code) {
  diagnostics.rejections[code] = (diagnostics.rejections[code] || 0) + 1;
}

function reject(code, reason, diagnostics) {
  return { ok: false, code, reason, diagnostics };
}

function quantile(sorted, ratio) {
  if (!sorted.length) return null;
  const position = (sorted.length - 1) * ratio;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  const weight = position - lower;
  return sorted[lower] + (sorted[upper] - sorted[lower]) * weight;
}

function median(values) {
  return quantile([...values].sort((a, b) => a - b), 0.5);
}

function round(value) {
  return Math.round(value * 10_000) / 10_000;
}

function assertPositive(value, name) {
  if (!Number.isFinite(value) || value <= 0) throw new RangeError(`${name} doit être > 0`);
}

function assertRatio(value, name) {
  if (!Number.isFinite(value) || value < 0 || value > 1) throw new RangeError(`${name} doit être entre 0 et 1`);
}
